import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TextField,
  TextArea,
  Select,
  Switch,
  Flex,
  Box,
  Text,
  PasswordField,
  Button,
  KeyValueEditor,
  TagBadge,
  CollapsibleSection,
  type SelectOption,
} from '@chatsdet/ui';
import {
  ArrowClockwise,
  Check,
  CopySimple,
  Envelope,
  Phone,
  ShieldCheck,
  Trash,
} from '@phosphor-icons/react';
import { API_URL, TEST_LOGIN_DOMAIN } from '../../config.js';
import { toApiError } from '../../lib/api-error';
import { useProvisionEmail } from './credential-dialog/useProvisionEmail';
import { SensitiveRevealField } from './SensitiveRevealField';
import { useCredentialTemplates } from '../../hooks/useCredentialTemplates';
import { BRAND } from '../../brand';
import { useAuth } from '../../contexts/AuthContext';
import {
  findSessionSeedOriginConflict,
  headerExtractorSchema,
  isEmailInTestSignInDomain,
} from '@chatsdet/types';
import { useTestSignInQuery } from './settings/test-sign-in/useTestSignIn';
import { TestSignInActivityPanel } from './settings/test-sign-in/TestSignInActivityPanel';
import { Link } from '@tanstack/react-router';

/**
 * One consistent rhythm for an advanced sub-group in this form. The heading is a
 * quiet UPPERCASE legend (muted, tracked) so it reads clearly as a *group* label
 * and never competes with the bold field labels (Username, Password, …) below
 * it. Then the control(s), then muted helper text. Groups are separated by the
 * parent's `flex flex-col gap-8` — NOT `space-y-*`, which doesn't apply under
 * this app's Tailwind build (the utility is inert at runtime).
 *
 * Spacing note: the cofactor brand sets a 14px root font, so rem spacing renders
 * ~12% tighter than nominal — values here are sized with that in mind.
 */
function FieldSection({
  title,
  help,
  icon,
  children,
}: {
  title: React.ReactNode;
  help?: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Flex align="center" gap="xs" className="mb-3">
        {icon}
        <Text size="xs" weight="semibold" className="uppercase tracking-wide text-muted-foreground">
          {title}
        </Text>
      </Flex>
      {children}
      {help && (
        <Text size="xs" className="text-muted-foreground mt-2 leading-relaxed">
          {help}
        </Text>
      )}
    </Box>
  );
}

/**
 * Card-style checkbox matching the Cofactor design handoff: a clickable card
 * with a checkmark box, title and description. Styling lives in
 * `packages/ui/src/styles.css` under `.cofactor .cred-check-card`.
 */
function CheckCard({
  isSelected,
  onChange,
  label,
  description,
  isDisabled,
}: {
  isSelected: boolean;
  onChange: (selected: boolean) => void;
  label: string;
  description: string;
  isDisabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="cred-check-card"
      data-on={isSelected}
      disabled={isDisabled}
      onClick={() => onChange(!isSelected)}
    >
      <span className="cred-cbox" />
      <span>
        <span className="cred-ct-t">{label}</span>
        <span className="cred-ct-d">{description}</span>
      </span>
    </button>
  );
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const isLikelyEmail = (s: string) => EMAIL_RE.test(s.trim());

type InboxRecipient = {
  id: string;
  email: string;
  label: string | null;
};

const API_LOGIN_CONTENT_TYPES: SelectOption[] = [
  { id: 'application/json', label: 'application/json — JSON object' },
  {
    id: 'application/x-www-form-urlencoded',
    label: 'application/x-www-form-urlencoded — Form fields',
  },
  { id: 'text/xml', label: 'text/xml — XML document' },
];

export type AuthProvider =
  | 'basic'
  | 'google'
  | 'google_oauth'
  | 'github'
  | 'microsoft'
  | 'api_key'
  | 'api_login'
  | 'api_basic'
  | 'twitter'
  | 'custom'
  | 'test_sign_in';

export type CredentialFormData = {
  name: string;
  authProvider: AuthProvider;
  data: Record<string, any>;
  environmentId?: string | null;
  /** Persona (credential template) this credential is bound to. Null = unassigned. */
  templateId?: string | null;
  /**
   * Values for the bound persona's declared variables (name → value). Sensitive values
   * arrive masked and round-trip unchanged. Injected at run time as `$var.USER.<name>`.
   */
  variableValues?: Record<string, string>;
  metadata?: Record<string, any>;
  isDefault?: boolean;
  tags?: string[];
  notes?: string;
  loginInstructions?: string;
  sessionVerificationGuidance?: string | null;
  createInboxRecipient?: boolean;
  /** Canary-provisioned email to attach as an inbox recipient for this credential. */
  provisionedEmail?: string;
  cloudflareAccessIntegrationId?: string | null;
  provisioningInstructions?: string;
  isProvisioned?: boolean;
};

type CloudflareAccessIntegration = {
  id: string;
  name: string;
  appDomain: string;
};

type Props = {
  propertyId?: string;
  credentialId?: string | null;
  value: CredentialFormData;
  onChange: (value: CredentialFormData) => void;
  disabled?: boolean;
  /**
   * Locks the persona select (used when the dialog was opened from a persona
   * group, so the new credential is created inside that persona).
   */
  templateLocked?: boolean;
  /**
   * Parent property is an MCP server — relabels the api_key key field as the
   * bearer token sent to the MCP endpoint. Labeling only; payload unchanged.
   */
  isMcpProperty?: boolean;
};

function CopyPasswordButton({ password }: { password: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box className="mb-[22px]">
      <Button
        variant="icon"
        size="sm"
        tooltip={copied ? 'Copied!' : 'Copy password'}
        onPress={handleCopy}
        iconOnly
        className="text-muted-foreground"
      >
        {copied ? <Check size={16} weight="bold" /> : <CopySimple size={16} />}
      </Button>
    </Box>
  );
}

function ApiLoginRequestBodyTemplateField({
  contentType,
  value,
  onChange,
  isDisabled,
}: {
  contentType: string;
  value: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
}) {
  const isFormEncoded = contentType.startsWith('application/x-www-form-urlencoded');
  const isXml = contentType.startsWith('text/xml');

  const placeholder = isXml
    ? '<credentials><username>{{username}}</username><password>{{password}}</password></credentials>'
    : isFormEncoded
      ? 'username={{username}}&password={{password}}'
      : '{"email":"{{username}}","pass":"{{password}}"}';

  const description = isXml
    ? 'Use {{fieldName}} placeholders and provide the complete XML document expected by the endpoint.'
    : isFormEncoded
      ? 'Use {{fieldName}} placeholders. Leave empty to send all login fields as URL-encoded form data.'
      : 'Use {{fieldName}} placeholders. Leave empty to send all login fields as JSON.';

  return (
    <TextArea
      label="Request Body Template (optional)"
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
      placeholder={placeholder}
      rows={3}
      className="font-mono"
      description={description}
    />
  );
}

/**
 * Simplified single-step credential form.
 * Shows essential fields upfront with advanced options collapsed.
 * Supports "Login User" (basic) and "API User" (api_key) modes.
 */
export function CredentialForm({
  propertyId,
  credentialId,
  value,
  onChange,
  disabled,
  templateLocked,
  isMcpProperty,
}: Props) {
  const name = BRAND.name;
  const { user } = useAuth();
  const smsEnabled = Boolean(user?.features?.smsAgentMessaging);
  const {
    provision: provisionInboxEmail,
    isProvisioning,
    error: provisionError,
  } = useProvisionEmail();

  const isApiKey = value.authProvider === 'api_key';
  const isApiBasic = value.authProvider === 'api_basic';
  const isApiLogin = value.authProvider === 'api_login';
  const isGoogleOauth = value.authProvider === 'google_oauth';
  const isTestSignIn = value.authProvider === 'test_sign_in';

  const testSignInQuery = useTestSignInQuery({ enabled: isTestSignIn });
  const testSignInProvider = testSignInQuery.data?.data.provider ?? null;
  const testSignInEmail = (value.data.email as string) || '';
  const testSignInEmailError =
    isTestSignIn && testSignInProvider && testSignInEmail.trim()
      ? isEmailInTestSignInDomain(testSignInEmail, testSignInProvider.emailDomain)
        ? null
        : `Must be in the test sign-in domain (@${testSignInProvider.emailDomain}).`
      : null;

  const handleDataChange = (key: string, val: string | Record<string, string>) => {
    onChange({
      ...value,
      data: { ...value.data, [key]: val },
    });
  };

  const handleMetadataChange = (key: string, val: string) => {
    onChange({
      ...value,
      metadata: { ...value.metadata, [key]: val },
    });
  };

  // Auto-detect inbox domain match from username
  const username = (value.data.username as string) || '';
  const inboxDomainMatch =
    !isApiKey && TEST_LOGIN_DOMAIN
      ? username.toLowerCase().endsWith(`@${TEST_LOGIN_DOMAIN.toLowerCase()}`)
      : false;

  // Auto-set createInboxRecipient when domain matches
  React.useEffect(() => {
    if (inboxDomainMatch && !value.createInboxRecipient) {
      onChange({ ...value, createInboxRecipient: true });
    } else if (!inboxDomainMatch && value.createInboxRecipient) {
      onChange({ ...value, createInboxRecipient: false });
    }
    // Only react to username changes, not the full value object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxDomainMatch]);

  const qc = useQueryClient();

  // Existing paired inbox recipients for this credential (edit mode only).
  const recipientsQuery = useQuery({
    queryKey: ['credential-recipients', credentialId],
    enabled: !!credentialId,
    queryFn: async (): Promise<InboxRecipient[]> => {
      const res = await fetch(
        `${API_URL}/email/recipients?credentialId=${encodeURIComponent(credentialId!)}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to load paired emails');
      const data = (await res.json()) as { ok: boolean; recipients: InboxRecipient[] };
      return data.recipients ?? [];
    },
  });
  const existingRecipients = recipientsQuery.data ?? [];

  const phoneQuery = useQuery({
    queryKey: ['credential-phone', credentialId],
    enabled: smsEnabled && !!credentialId,
    queryFn: async (): Promise<{ phoneNumber: string } | null> => {
      const response = await fetch(`${API_URL}/messaging/credentials/${credentialId}/phone`, {
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response);
      const payload = (await response.json()) as { phone: { phoneNumber: string } | null };
      return payload.phone;
    },
  });
  const provisionPhone = useMutation({
    mutationFn: async () => {
      if (!credentialId || !propertyId)
        throw new Error('Save the credential before provisioning a phone number.');
      const response = await fetch(`${API_URL}/messaging/credentials/${credentialId}/phone`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ propertyId }),
      });
      if (!response.ok) throw await toApiError(response);
      return response.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credential-phone', credentialId] }),
  });

  const removeRecipientMutation = useMutation({
    mutationFn: async (recipientId: string) => {
      const res = await fetch(`${API_URL}/email/recipients/${recipientId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to remove paired email');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credential-recipients', credentialId] });
    },
  });

  // Fetch Cloudflare Access integrations
  const cfIntegrationsQuery = useQuery({
    queryKey: ['cloudflare-access-integrations'],
    queryFn: async (): Promise<{ ok: boolean; data: CloudflareAccessIntegration[] }> => {
      const res = await fetch(`${API_URL}/integrations/cloudflare-access`, {
        credentials: 'include',
      });
      if (!res.ok) {
        return { ok: false, data: [] };
      }
      return res.json();
    },
  });

  const cfIntegrations = cfIntegrationsQuery.data?.data ?? [];
  const hasCfIntegrations = cfIntegrations.length > 0;

  const environmentsQuery = useQuery({
    queryKey: ['property-environments', propertyId],
    enabled: !!propertyId,
    queryFn: async (): Promise<{
      items: Array<{ id: string; name: string; isDefault: boolean }>;
    }> => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/environments`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load environments');
      return res.json();
    },
  });

  const environmentOptions: SelectOption[] = React.useMemo(() => {
    const items = environmentsQuery.data?.items ?? [];
    return [
      { id: 'none', label: 'All environments' },
      ...items.map((env) => ({
        id: env.id,
        label: env.isDefault ? `${env.name} (Default)` : env.name,
      })),
    ];
  }, [environmentsQuery.data?.items]);

  const templatesQuery = useCredentialTemplates(propertyId ?? null);
  const personaOptions: SelectOption[] = React.useMemo(() => {
    const items = templatesQuery.data ?? [];
    return [
      { id: 'none', label: 'No persona' },
      ...items.map((t) => ({
        id: t.id,
        label: t.name,
        description: t.description ?? undefined,
      })),
    ];
  }, [templatesQuery.data]);
  // Show the persona select when the property has personas, or when the value
  // is locked/pre-set (so the locked persona is always visible).
  const showPersonaSelect = !!propertyId && (personaOptions.length > 1 || !!value.templateId);

  // Variables declared on the currently-bound persona — rendered as fill fields.
  const selectedPersonaVariables = React.useMemo(() => {
    if (!value.templateId) return [];
    const persona = (templatesQuery.data ?? []).find((t) => t.id === value.templateId);
    return persona?.variableDefinitions ?? [];
  }, [templatesQuery.data, value.templateId]);

  // Count configured fields per advanced group so each collapsed section can
  // surface a badge (lets users see what's set without expanding).
  const loginBehaviorCount = [
    value.loginInstructions,
    value.sessionVerificationGuidance,
    value.isProvisioned === false,
  ].filter(Boolean).length;

  const authCount = [
    value.cloudflareAccessIntegrationId,
    value.data.httpCredentials?.username,
    value.data.httpCredentials?.password,
    value.data.customHeaders && Object.keys(value.data.customHeaders).length > 0,
    Array.isArray(value.metadata?.headerExtractors) && value.metadata.headerExtractors.length > 0,
    value.data.mfaSecret,
  ].filter(Boolean).length;

  const envDefaultsCount = [value.templateId, value.environmentId, value.isDefault].filter(
    Boolean
  ).length;

  const notesCount = value.notes ? 1 : 0;

  return (
    <Flex direction="column" gap="md" className="w-full">
      {/* Essential Fields */}
      <TextField
        label="Name"
        value={value.name}
        onChange={(v) => onChange({ ...value, name: v })}
        isDisabled={disabled}
        isRequired
        placeholder={
          isApiKey || isApiBasic ? 'e.g., Production API Key' : 'e.g., Test User, Admin Account'
        }
        description="A friendly name to identify this credential"
      />

      {isApiKey ? (
        <>
          <PasswordField
            label={isMcpProperty ? 'Bearer token' : 'API Key'}
            value={value.data.key || ''}
            onChange={(v) => handleDataChange('key', v)}
            isDisabled={disabled}
            isRequired
            placeholder={isMcpProperty ? 'Enter bearer token' : 'Enter API key or token'}
            description={
              isMcpProperty ? (
                <>
                  Sent as <code>Authorization: Bearer &lt;token&gt;</code> to the MCP server.
                </>
              ) : undefined
            }
          />
          <TextField
            label="Header Name"
            value={value.metadata?.headerName || ''}
            onChange={(v) => handleMetadataChange('headerName', v)}
            isDisabled={disabled}
            placeholder="Authorization"
            description="HTTP header to send the key in (default: Authorization)"
          />
          <TextField
            label="Prefix"
            value={value.data.prefix || ''}
            onChange={(v) => handleDataChange('prefix', v)}
            isDisabled={disabled}
            placeholder="Bearer"
            description="Value prefix before the key (default: Bearer)"
          />
        </>
      ) : isApiBasic ? (
        <>
          <TextField
            label="Username"
            value={value.data.username || ''}
            onChange={(v) => handleDataChange('username', v)}
            isDisabled={disabled}
            isRequired
            placeholder="Enter username"
          />
          <PasswordField
            label="Password"
            value={value.data.password || ''}
            onChange={(v) => handleDataChange('password', v)}
            isDisabled={disabled}
            isRequired
            placeholder="Enter password"
          />
        </>
      ) : isGoogleOauth ? (
        <>
          <TextField
            label="Client ID"
            value={value.data.clientId || ''}
            onChange={(v) => handleDataChange('clientId', v)}
            isDisabled={disabled}
            isRequired
            placeholder="1234567890-abc.apps.googleusercontent.com"
            description="From your app's own Google OAuth client — the one your app validates tokens against."
          />
          <PasswordField
            label="Client Secret"
            value={value.data.clientSecret || ''}
            onChange={(v) => handleDataChange('clientSecret', v)}
            isDisabled={disabled}
            isRequired
            placeholder="Enter client secret"
          />
          <PasswordField
            label="Refresh Token"
            value={value.data.refreshToken || ''}
            onChange={(v) => handleDataChange('refreshToken', v)}
            isDisabled={disabled}
            isRequired
            placeholder="Enter the test account's refresh token"
          />
          <RefreshTokenHelp />
        </>
      ) : isTestSignIn ? (
        <TestSignInCredentialFields
          value={value}
          disabled={disabled}
          credentialId={credentialId}
          testSignInProvider={testSignInProvider}
          testSignInProviderLoading={testSignInQuery.isLoading}
          testSignInEmail={testSignInEmail}
          testSignInEmailError={testSignInEmailError}
          handleDataChange={handleDataChange}
        />
      ) : isApiLogin ? (
        <>
          <TextField
            label="Token Endpoint URL"
            value={value.metadata?.tokenUrl || ''}
            onChange={(v) => handleMetadataChange('tokenUrl', v)}
            isDisabled={disabled}
            isRequired
            placeholder="https://api.example.com/auth/token"
            description="The API endpoint that returns a token when called with credentials"
          />

          <Select
            label="HTTP Method"
            items={[
              { id: 'POST', label: 'POST — Send fields in request body' },
              { id: 'GET', label: 'GET — Send fields as query parameters' },
            ]}
            selectedKey={value.metadata?.tokenRequestMethod || 'POST'}
            onSelectionChange={(key) => handleMetadataChange('tokenRequestMethod', String(key))}
            isDisabled={disabled}
          />

          <Box>
            <Text size="sm" weight="medium" className="mb-2">
              Token Request Headers
            </Text>
            <Text size="xs" className="text-muted-foreground mb-2">
              Headers sent only to the token endpoint, not to subsequent API requests (for example,{' '}
              <code>SOAPAction: login</code>).
            </Text>
            <KeyValueEditor
              value={value.data.tokenRequestHeaders || {}}
              onChange={(headers: Record<string, string>) =>
                handleDataChange('tokenRequestHeaders', headers)
              }
              disabled={disabled}
              placeholder={{ key: 'Header name', value: 'Header value' }}
              addButtonLabel="Add Header"
            />
          </Box>

          {(value.metadata?.tokenRequestMethod || 'POST') === 'POST' && (
            <Select
              label="Request Content-Type"
              items={API_LOGIN_CONTENT_TYPES}
              selectedKey={value.metadata?.tokenRequestContentType || 'application/json'}
              onSelectionChange={(key) =>
                handleMetadataChange('tokenRequestContentType', String(key))
              }
              isDisabled={disabled}
              description="Content type sent with the POST request body"
            />
          )}

          <Box>
            <Text size="sm" weight="medium" className="mb-2">
              Login Fields
            </Text>
            <Text size="xs" className="text-muted-foreground mb-2">
              {(value.metadata?.tokenRequestMethod || 'POST') === 'GET'
                ? 'Key-value pairs appended as query parameters to the URL'
                : 'Key-value pairs sent in the request body (e.g., username/password, email/api_key)'}
            </Text>
            <KeyValueEditor
              value={value.data.loginFields || { username: '', password: '' }}
              onChange={(fields: Record<string, string>) => handleDataChange('loginFields', fields)}
              disabled={disabled}
              placeholder={{ key: 'Field name', value: 'Field value' }}
              addButtonLabel="Add Field"
            />
          </Box>

          {(value.metadata?.tokenRequestMethod || 'POST') !== 'GET' && (
            <ApiLoginRequestBodyTemplateField
              contentType={value.metadata?.tokenRequestContentType || 'application/json'}
              value={value.metadata?.tokenRequestBodyTemplate || ''}
              onChange={(template) => handleMetadataChange('tokenRequestBodyTemplate', template)}
              isDisabled={disabled}
            />
          )}
        </>
      ) : (
        <>
          <Box>
            <TextField
              label="Username"
              value={value.data.username || ''}
              onChange={(v) => handleDataChange('username', v)}
              isDisabled={disabled}
              isRequired
              placeholder="Enter username or email"
            />
            {inboxDomainMatch && (
              <Flex align="center" gap="xs" className="mt-1.5">
                <Envelope size={14} className="text-primary" />
                <Text size="xs" className="text-primary">
                  Email inbox will be created automatically
                </Text>
              </Flex>
            )}
          </Box>

          {(() => {
            const hasExistingRecipient = existingRecipients.length > 0;
            const usernameLooksLikeEmail = isLikelyEmail(username);
            const showProvisionPrompt =
              !!TEST_LOGIN_DOMAIN &&
              !inboxDomainMatch &&
              !hasExistingRecipient &&
              username.trim().length > 0 &&
              !usernameLooksLikeEmail;

            if (hasExistingRecipient) {
              const recipient = existingRecipients[0]!;
              return (
                <Box className="rounded-lg border border-border bg-muted/30 px-3 py-2">
                  <Flex align="center" justify="between" gap="sm">
                    <Flex align="center" gap="xs">
                      <Envelope size={16} className="text-primary" />
                      <Box>
                        <Text size="sm" weight="medium">
                          Paired inbox email
                        </Text>
                        <Text size="xs" className="text-muted-foreground">
                          {recipient.email}
                        </Text>
                      </Box>
                    </Flex>
                    <Button
                      variant="icon"
                      size="sm"
                      iconOnly
                      tooltip="Remove paired email"
                      onPress={() => removeRecipientMutation.mutate(recipient.id)}
                      isDisabled={disabled || removeRecipientMutation.isPending}
                    >
                      <Trash size={16} />
                    </Button>
                  </Flex>
                </Box>
              );
            }

            if (value.provisionedEmail) {
              return (
                <Box className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
                  <Flex align="center" justify="between" gap="sm">
                    <Flex align="center" gap="xs">
                      <Envelope size={16} className="text-primary" />
                      <Box>
                        <Text size="sm" weight="medium">
                          Provisioned email (created on save)
                        </Text>
                        <Text size="xs" className="text-muted-foreground">
                          {value.provisionedEmail}
                        </Text>
                      </Box>
                    </Flex>
                    <Flex gap="xs">
                      <Button
                        variant="icon"
                        size="sm"
                        iconOnly
                        tooltip="Regenerate email"
                        onPress={async () => {
                          const result = await provisionInboxEmail();
                          if (result) onChange({ ...value, provisionedEmail: result.email });
                        }}
                        isDisabled={disabled || isProvisioning}
                      >
                        <ArrowClockwise size={16} />
                      </Button>
                      <Button
                        variant="icon"
                        size="sm"
                        iconOnly
                        tooltip="Don't provision an email"
                        onPress={() => onChange({ ...value, provisionedEmail: undefined })}
                        isDisabled={disabled}
                      >
                        <Trash size={16} />
                      </Button>
                    </Flex>
                  </Flex>
                </Box>
              );
            }

            if (!showProvisionPrompt) return null;

            return (
              <Box className="rounded-lg border border-border bg-muted/30 px-3 py-3">
                <Flex align="start" gap="sm">
                  <Envelope size={18} className="text-primary mt-0.5" />
                  <Box className="flex-1">
                    <Flex align="center" justify="between" gap="sm">
                      <Text size="sm" weight="medium">
                        Provision an email inbox
                      </Text>
                      <Switch
                        isSelected={false}
                        isDisabled={disabled || isProvisioning}
                        onChange={async (selected) => {
                          if (!selected) return;
                          const result = await provisionInboxEmail();
                          if (result) onChange({ ...value, provisionedEmail: result.email });
                        }}
                      />
                    </Flex>
                    <Text size="xs" className="text-muted-foreground mt-2 leading-relaxed">
                      This login uses a username, not an email. Pair it with a {name}-provisioned
                      inbox for signup confirmations, password resets, and 2FA codes.
                    </Text>
                    {provisionError && (
                      <Text size="xs" className="text-red-500 mt-1">
                        {provisionError}
                      </Text>
                    )}
                  </Box>
                </Flex>
              </Box>
            );
          })()}

          {smsEnabled && credentialId && propertyId && (
            <Box className="rounded-lg border border-border bg-muted/30 px-3 py-3">
              <Flex align="center" justify="between" gap="sm">
                <Flex align="center" gap="sm">
                  <Phone size={18} className="text-primary" />
                  <Box>
                    <Text size="sm" weight="medium">
                      SMS phone number
                    </Text>
                    <Text size="xs" className="text-muted-foreground">
                      {phoneQuery.data?.phoneNumber ?? 'No number assigned'}
                    </Text>
                  </Box>
                </Flex>
                {!phoneQuery.data && (
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={() => provisionPhone.mutate()}
                    isDisabled={disabled || provisionPhone.isPending}
                  >
                    {provisionPhone.isPending ? 'Provisioning…' : 'Provision number'}
                  </Button>
                )}
              </Flex>
              {provisionPhone.error && (
                <Text size="xs" className="mt-2 text-red-500">
                  {provisionPhone.error instanceof Error
                    ? provisionPhone.error.message
                    : 'Failed to provision phone number'}
                </Text>
              )}
            </Box>
          )}

          <Box>
            <Flex align="end" gap="xs">
              <Box className="flex-1">
                <PasswordField
                  label="Password"
                  value={value.data.password || ''}
                  onChange={(v) => handleDataChange('password', v)}
                  isDisabled={disabled}
                  placeholder="Enter password (optional for OTP)"
                  description="Leave blank if using one-time password or email code"
                />
              </Box>
              {value.data.password && <CopyPasswordButton password={value.data.password} />}
            </Flex>
          </Box>
        </>
      )}

      <Box>
        <Text size="sm" weight="medium" className="mb-2">
          Tags
        </Text>
        <TextField
          value={(value.tags || []).join(', ')}
          onChange={(v) => {
            const tags = v
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean);
            onChange({ ...value, tags });
          }}
          isDisabled={disabled}
          placeholder="admin, read-only, billing"
          description="Comma-separated tags for organization"
        />
        {value.tags && value.tags.length > 0 && (
          <Flex gap="xs" wrap className="mt-2">
            {value.tags.map((tag, idx) => (
              <TagBadge name={tag} color="gray" hideDot key={idx} className="text-xs" />
            ))}
          </Flex>
        )}
      </Box>

      {/* Advanced options — grouped into independently-collapsible sections so
          the form stays scannable instead of one monolithic blob. Uses the kit's
          default CollapsibleSection look (hairline-separated rows). */}
      <Box className="mt-2">
        {!isApiKey && !isApiBasic && (
          <CollapsibleSection label="Login behavior" count={loginBehaviorCount}>
            <Box className="flex flex-col gap-8 pt-1">
              <TextArea
                label="Login Instructions"
                value={value.loginInstructions || ''}
                onChange={(v) => onChange({ ...value, loginInstructions: v })}
                isDisabled={disabled}
                placeholder="e.g., Click 'Sign in with password' if prompted for email code"
                rows={3}
                description="Help guide the login process for complex flows"
              />

              <TextArea
                label="Session verification guidance"
                value={value.sessionVerificationGuidance || ''}
                onChange={(v) =>
                  onChange({ ...value, sessionVerificationGuidance: v.trim() ? v : null })
                }
                isDisabled={disabled}
                placeholder={
                  'e.g., The Settings page is usable even when the header says “Not authenticated”.'
                }
                rows={3}
                description="Explain what confirms usable access and which authentication labels may be misleading."
              />

              <Box>
                {(() => {
                  const onProvisioningChange = (checked: boolean) =>
                    onChange({
                      ...value,
                      isProvisioned: !checked,
                      provisioningInstructions: checked
                        ? value.provisioningInstructions || ''
                        : undefined,
                    });
                  const label = 'Account needs provisioning';
                  const description =
                    'This account needs to be created/activated before first login';
                  return (
                    <CheckCard
                      isSelected={value.isProvisioned === false}
                      onChange={onProvisioningChange}
                      isDisabled={disabled}
                      label={label}
                      description={description}
                    />
                  );
                })()}
                {value.isProvisioned === false && (
                  <Box className="mt-2">
                    <TextArea
                      value={value.provisioningInstructions || ''}
                      onChange={(v) => onChange({ ...value, provisioningInstructions: v })}
                      isDisabled={disabled}
                      placeholder="e.g., Check inbox for invite email, click the invite link, complete account registration form"
                      rows={3}
                      description="Instructions for creating/activating the account before first login"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </CollapsibleSection>
        )}

        {!isApiKey && !isApiBasic && (
          <CollapsibleSection label="Authentication & headers" count={authCount}>
            <Box className="flex flex-col gap-8 pt-1">
              {/* Cloudflare Access Integration */}
              {hasCfIntegrations && (
                <FieldSection
                  icon={<ShieldCheck size={16} className="text-muted-foreground" />}
                  title="Cloudflare Access Authentication"
                  help="Use a Cloudflare Access service token instead of AI-driven login"
                >
                  <Select
                    aria-label="Cloudflare Access Integration"
                    selectedKey={value.cloudflareAccessIntegrationId ?? 'none'}
                    items={[
                      { id: 'none', label: 'None - use AI login' },
                      ...cfIntegrations.map((int) => ({
                        id: int.id,
                        label: `${int.name} (${int.appDomain})`,
                      })),
                    ]}
                    onSelectionChange={(key) => {
                      onChange({
                        ...value,
                        cloudflareAccessIntegrationId: key === 'none' ? null : String(key),
                      });
                    }}
                    isDisabled={disabled}
                  />
                </FieldSection>
              )}

              <FieldSection
                title="HTTP Authentication"
                help="For sites that use browser-level HTTP Basic Authentication popups."
              >
                <Flex direction="column" gap="sm">
                  <TextField
                    label="Username"
                    value={value.data.httpCredentials?.username || ''}
                    onChange={(v) => {
                      const current = value.data.httpCredentials || {
                        username: '',
                        password: '',
                      };
                      handleDataChange('httpCredentials', { ...current, username: v });
                    }}
                    isDisabled={disabled}
                    placeholder="HTTP auth username"
                  />
                  <PasswordField
                    label="Password"
                    value={value.data.httpCredentials?.password || ''}
                    onChange={(v) => {
                      const current = value.data.httpCredentials || {
                        username: '',
                        password: '',
                      };
                      handleDataChange('httpCredentials', { ...current, password: v });
                    }}
                    isDisabled={disabled}
                    placeholder="HTTP auth password"
                  />
                </Flex>
              </FieldSection>

              <FieldSection
                title="Custom HTTP Headers"
                help="Add headers to bypass bot detection (e.g., User-Agent). These are encrypted with your credentials."
              >
                <KeyValueEditor
                  value={value.data.customHeaders || {}}
                  onChange={(headers: Record<string, string>) =>
                    handleDataChange('customHeaders', headers)
                  }
                  disabled={disabled}
                  placeholder={{ key: 'Header-Name', value: 'Header Value' }}
                  addButtonLabel="Add Header"
                />
              </FieldSection>

              <HeaderExtractorsEditor
                value={value.metadata?.headerExtractors}
                onChange={(extractors) => {
                  const nextMetadata = { ...value.metadata };
                  if (extractors === undefined) {
                    delete nextMetadata.headerExtractors;
                  } else {
                    nextMetadata.headerExtractors = extractors;
                  }
                  onChange({ ...value, metadata: nextMetadata });
                }}
                disabled={disabled}
              />

              {isGoogleOauth && (
                <SessionSeedsEditor
                  value={value.metadata?.sessionSeeds}
                  onChange={(seeds) => {
                    const nextMetadata = { ...value.metadata };
                    if (seeds === undefined) {
                      delete nextMetadata.sessionSeeds;
                    } else {
                      nextMetadata.sessionSeeds = seeds;
                    }
                    onChange({ ...value, metadata: nextMetadata });
                  }}
                  disabled={disabled}
                  scope="credential"
                />
              )}

              <FieldSection
                title="MFA Secret (Optional)"
                help="For sites using authenticator apps (Google Authenticator, Authy). Find this in 'Can't scan QR code?' during setup."
              >
                <PasswordField
                  aria-label="MFA Secret"
                  value={value.data.mfaSecret || ''}
                  onChange={(v) => handleDataChange('mfaSecret', v)}
                  isDisabled={disabled}
                  placeholder="Base32 secret from authenticator setup"
                />
              </FieldSection>
            </Box>
          </CollapsibleSection>
        )}

        <CollapsibleSection label="Persona, environment & defaults" count={envDefaultsCount}>
          <Box className="flex flex-col gap-8 pt-1">
            {showPersonaSelect && (
              <Box>
                <Select
                  label={templateLocked ? 'Persona' : 'Persona (optional)'}
                  items={personaOptions}
                  selectedKey={value.templateId ?? 'none'}
                  onSelectionChange={(key) =>
                    onChange({
                      ...value,
                      templateId: key === 'none' ? null : String(key),
                    })
                  }
                  isDisabled={disabled || templateLocked || templatesQuery.isLoading}
                />
                <Text size="xs" className="text-muted-foreground mt-2 leading-relaxed">
                  {templateLocked
                    ? 'This credential is being created inside this persona.'
                    : 'Personas group per-environment credentials so a test resolves the right login automatically.'}
                </Text>
              </Box>
            )}

            {selectedPersonaVariables.length > 0 && (
              <Box className="flex flex-col gap-4">
                <Box>
                  <Text size="sm" weight="medium">
                    Persona variables
                  </Text>
                  <Text size="xs" className="text-muted-foreground leading-relaxed">
                    Fill this credential's values for the persona's declared variables. Injected at
                    run time as <code className="text-xs">$var.USER.&lt;NAME&gt;</code>.
                  </Text>
                </Box>
                {selectedPersonaVariables.map((def) => {
                  const fieldValue = value.variableValues?.[def.name] ?? '';
                  const setFieldValue = (v: string) =>
                    onChange({
                      ...value,
                      variableValues: { ...(value.variableValues ?? {}), [def.name]: v },
                    });
                  return def.sensitive ? (
                    <SensitiveRevealField
                      key={def.name}
                      revealUrl={
                        propertyId && credentialId
                          ? `${API_URL}/org/properties/${encodeURIComponent(propertyId)}/credentials/${encodeURIComponent(credentialId)}/variables/${encodeURIComponent(def.name)}/reveal`
                          : null
                      }
                      label={def.name}
                      value={fieldValue}
                      onChange={setFieldValue}
                      description={def.description}
                      isDisabled={disabled}
                    />
                  ) : (
                    <TextField
                      key={def.name}
                      label={def.name}
                      value={fieldValue}
                      onChange={setFieldValue}
                      description={def.description}
                      isDisabled={disabled}
                    />
                  );
                })}
              </Box>
            )}

            {propertyId && (
              <Select
                label="Environment (optional)"
                items={environmentOptions}
                selectedKey={value.environmentId ?? 'none'}
                onSelectionChange={(key) =>
                  onChange({
                    ...value,
                    environmentId: key === 'none' ? null : String(key),
                  })
                }
                isDisabled={disabled || environmentsQuery.isLoading}
              />
            )}

            <CheckCard
              isSelected={value.isDefault || false}
              onChange={(checked) => onChange({ ...value, isDefault: checked })}
              isDisabled={disabled}
              label="Set as default credential"
              description="Automatically selected for new tests"
            />
          </Box>
        </CollapsibleSection>

        <CollapsibleSection label="Notes" count={notesCount}>
          <Box className="pt-1">
            <TextArea
              value={value.notes || ''}
              onChange={(v) => onChange({ ...value, notes: v })}
              isDisabled={disabled}
              placeholder="Additional notes about this credential"
              rows={2}
            />
          </Box>
        </CollapsibleSection>
      </Box>
    </Flex>
  );
}

const EXTRACTOR_PLACEHOLDER = `[
  {
    "name": "accountId",
    "header": "Accountid",
    "source": {
      "kind": "localStorage",
      "origin": "https://app.example.com",
      "key": "selectedAccount",
      "jsonPath": "$.accountId"
    }
  },
  {
    "name": "fbToken",
    "header": "Authentication",
    "prefix": "Bearer ",
    "source": {
      "kind": "indexedDB",
      "database": "firebaseLocalStorageDb",
      "store": "firebaseLocalStorage",
      "key": "firebase:authUser:<apiKey>:[DEFAULT]",
      "jsonPath": "$.value.stsTokenManager.accessToken"
    },
    "refresh": { "kind": "firebase" }
  }
]`;

/**
 * How to obtain a Google refresh token for a test account.
 *
 * There is deliberately no hosted "Connect with Google" button: the consent
 * screen would authenticate whoever is sitting at this browser (an operator),
 * not the test persona we need to impersonate. Getting a token for the right
 * identity means signing in as that account — which is exactly this flow.
 */
function RefreshTokenHelp() {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="rounded-md border border-border bg-muted/40 p-3">
      <Button
        variant="link"
        size="sm"
        onPress={() => setOpen((v) => !v)}
        className="p-0"
      >
        {open ? 'Hide' : 'How do I get a refresh token?'}
      </Button>
      {open && (
        <ol className="mt-2 list-decimal space-y-1.5 pl-4 text-xs text-muted-foreground">
          <li>
            Google Cloud Console → Credentials → open your app&apos;s{' '}
            <strong>existing</strong> OAuth 2.0 Web application client. Do not create a new
            one — your app validates the token against this client&apos;s ID.
          </li>
          <li>
            Add <code className="font-mono">https://developers.google.com/oauthplayground</code>{' '}
            to its Authorized redirect URIs.
          </li>
          <li>
            Open the{' '}
            <a
              href="https://developers.google.com/oauthplayground"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              OAuth Playground
            </a>
            , click the gear icon, tick <em>Use your own OAuth credentials</em>, and paste the
            Client ID and Secret.
          </li>
          <li>
            Select the <code className="font-mono">openid</code>,{' '}
            <code className="font-mono">email</code> and{' '}
            <code className="font-mono">profile</code> scopes (plus any your app needs),
            authorize <strong>as the test account</strong>, then exchange the authorization
            code for tokens.
          </li>
          <li>Copy the refresh token into the field above.</li>
          <li>
            Refresh tokens are per-account — repeat for each test persona. If your OAuth
            consent screen is still in <strong>Testing</strong> mode, the token expires after
            7 days; publish the app (or add the account as an internal user) for one that
            lasts.
          </li>
        </ol>
      )}
    </div>
  );
}

/**
 * Fields for a `test_sign_in` credential — the credential IS the test user
 * (email + name). Cofactor signs this identity in deterministically through
 * the org's OpenID provider (Settings → Test sign-in), so there is no
 * password to collect. When editing an existing credential, shows the
 * recent-sign-in diagnostics from that provider so a failed "Save & Test"
 * points at the exact refusal (e.g. a redirect_uri mismatch).
 */
function TestSignInCredentialFields({
  value,
  disabled,
  credentialId,
  testSignInProvider,
  testSignInProviderLoading,
  testSignInEmail,
  testSignInEmailError,
  handleDataChange,
}: {
  value: CredentialFormData;
  disabled?: boolean;
  credentialId?: string | null;
  testSignInProvider: { emailDomain: string } | null;
  testSignInProviderLoading: boolean;
  testSignInEmail: string;
  testSignInEmailError: string | null;
  handleDataChange: (key: string, val: string) => void;
}) {
  if (!testSignInProviderLoading && !testSignInProvider) {
    return (
      <Box className="rounded-lg border border-border bg-muted/30 px-3 py-3">
        <Text size="sm" weight="medium">
          Test sign-in isn&apos;t set up yet
        </Text>
        <Text size="xs" className="text-muted-foreground mt-1">
          An org admin needs to set up a provider first.{' '}
          <Link to="/settings/test-sign-in" className="text-primary hover:underline">
            Go to Settings → Test sign-in
          </Link>
          .
        </Text>
      </Box>
    );
  }

  return (
    <>
      <TextField
        label="Email"
        value={testSignInEmail}
        onChange={(v) => handleDataChange('email', v)}
        isDisabled={disabled}
        isRequired
        placeholder={testSignInProvider ? `ada@${testSignInProvider.emailDomain}` : 'Enter email'}
        description={
          testSignInProvider
            ? `Must be in the test sign-in domain (@${testSignInProvider.emailDomain}).`
            : undefined
        }
        errorMessage={testSignInEmailError ?? undefined}
      />
      <TextField
        label="Test user's name"
        value={(value.data.name as string) || ''}
        onChange={(v) => handleDataChange('name', v)}
        isDisabled={disabled}
        isRequired
        placeholder="Ada Lovelace"
        description="Sent as the identity's display name (the OIDC 'name' claim)."
      />
      <TextField
        label="Start URL (optional)"
        value={(value.data.startUrl as string) || ''}
        onChange={(v) => handleDataChange('startUrl', v)}
        isDisabled={disabled}
        placeholder="/auth/cofactor"
        description="A path or full URL where 'Sign in with Cofactor' starts. Leave blank to use the environment's login page."
      />
      <TextField
        label="Sign-in button selector (optional)"
        value={(value.data.signInButtonSelector as string) || ''}
        onChange={(v) => handleDataChange('signInButtonSelector', v)}
        isDisabled={disabled}
        placeholder="button[data-provider='cofactor']"
        description="For apps (like Auth.js) where sign-in starts from a button click rather than a GET link."
      />
      {credentialId && (
        <Box className="mt-2">
          <Text size="xs" weight="semibold" className="uppercase tracking-wide text-muted-foreground mb-2">
            Recent sign-ins
          </Text>
          <TestSignInActivityPanel credentialId={credentialId} limit={5} compact />
        </Box>
      )}
    </>
  );
}

const SESSION_SEED_PLACEHOLDER = `[
  {
    "name": "appToken",
    "target": {
      "kind": "localStorage",
      "origin": "https://app.example.com",
      "key": "app-token"
    },
    "valueTemplate": "{{id_token}}"
  },
  {
    "name": "googleSession",
    "target": {
      "kind": "localStorage",
      "origin": "https://app.example.com",
      "key": "googleAuth"
    },
    "valueTemplate": "{\"token\":\"{{response.id_token}}\",\"user\":{\"googleId\":\"{{profile.sub}}\",\"email\":\"{{profile.email}}\"}}"
  }
]`;

/**
 * Editor for `metadata.sessionSeeds` — the write-direction mirror of the header
 * extractors above. Same authoring model on purpose.
 */
export function SessionSeedsEditor(props: {
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  /** Property-level editors say "this app"; the credential one says "this user". */
  scope?: 'property' | 'credential';
}) {
  const scope = props.scope ?? 'credential';
  return (
    <JsonSpecEditor
      value={props.value}
      onChange={props.onChange}
      disabled={props.disabled}
      ariaLabel="Session seeds JSON"
      placeholder={SESSION_SEED_PLACEHOLDER}
      title={scope === 'property' ? 'Session Seeds' : 'Session Seeds (override)'}
      help={
        scope === 'property'
          ? 'Declares where this app keeps its session, so a Google (token) credential can write a freshly minted token straight into storage state — no login UI. Placeholders: {{id_token}}, {{access_token}}, {{expires_at}} (also _ms / _s), {{profile.<path>}} for any profile claim, and {{response.<path>}} to reach any field of the raw token response, e.g. {{response.data.tokens[0].value}}.'
          : "Overrides the application's session seeds for this one test user. Leave empty to use the application-level configuration."
      }
      validate={validateSessionSeedShape}
      emptyLabel="Expected a JSON array of session seed objects."
    />
  );
}

/**
 * Inline shape check mirroring the server-side schema — per-entry shape, plus
 * the one cross-entry rule.
 *
 * The cross-entry rule matters here specifically: without it the editor happily
 * saves a configuration the run time refuses, and the user only finds out when
 * a test fails. `findSessionSeedOriginConflict` is the same function the server
 * schema and the renderer use, so the three cannot drift.
 */
function validateSessionSeedShape(parsed: unknown[]): string | null {
  for (const [i, entry] of parsed.entries()) {
    if (!entry || typeof entry !== 'object') return `Entry ${i}: must be an object`;
    const e = entry as Record<string, unknown>;
    if (typeof e.name !== 'string' || !e.name) return `Entry ${i}: "name" is required`;
    if (typeof e.valueTemplate !== 'string' || !e.valueTemplate)
      return `Entry ${i}: "valueTemplate" is required`;
    if (!e.target || typeof e.target !== 'object') return `Entry ${i}: "target" is required`;
    const t = e.target as Record<string, unknown>;
    if (t.kind === 'localStorage') {
      for (const f of ['origin', 'key']) {
        if (typeof t[f] !== 'string' || !t[f])
          return `Entry ${i}: localStorage target needs "${f}"`;
      }
    } else if (t.kind === 'cookie') {
      for (const f of ['domain', 'name']) {
        if (typeof t[f] !== 'string' || !t[f]) return `Entry ${i}: cookie target needs "${f}"`;
      }
    } else if (t.kind === 'indexedDB') {
      // `origin` is required here but not on the read-direction extractor:
      // IndexedDB restore has to navigate to an origin first, and an
      // IndexedDB-only seed carries no other origin hint.
      for (const f of ['origin', 'database', 'store', 'key']) {
        if (typeof t[f] !== 'string' || !t[f])
          return `Entry ${i}: indexedDB target needs "${f}"`;
      }
    } else if (t.kind === 'sessionStorage') {
      // Rejected deliberately: Playwright storage state cannot carry
      // sessionStorage, so such a seed would silently write nothing.
      return `Entry ${i}: sessionStorage is not supported — use localStorage or a cookie`;
    } else {
      return `Entry ${i}: target.kind must be localStorage, cookie, or indexedDB`;
    }
  }

  return findSessionSeedOriginConflict(
    parsed as Array<{ target: { kind: string; origin?: string } }>
  );
}

/**
 * Shared raw-JSON spec editor. Validates on blur by attempting JSON.parse and
 * running a caller-supplied shape check. Doesn't re-implement the Zod schema —
 * the server still rejects bad shapes on save; this is a fast first-line check
 * for the editing user.
 */
function JsonSpecEditor(props: {
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  title: string;
  help: string;
  placeholder: string;
  ariaLabel: string;
  emptyLabel: string;
  validate: (parsed: unknown[]) => string | null;
}) {
  const initial = React.useMemo(
    () => (props.value === undefined ? '' : JSON.stringify(props.value, null, 2)),
    // Only re-stringify when the source toggles between undefined/defined,
    // not on every keystroke (otherwise typing fights the controlled state).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.value === undefined]
  );
  const [text, setText] = React.useState(initial);
  const [error, setError] = React.useState<string | null>(null);

  const commit = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      props.onChange(undefined);
      setError(null);
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return;
    }
    if (!Array.isArray(parsed)) {
      setError(props.emptyLabel);
      return;
    }
    const shapeError = props.validate(parsed);
    if (shapeError) {
      setError(shapeError);
      return;
    }
    setError(null);
    props.onChange(parsed);
  };

  return (
    <FieldSection title={props.title} help={props.help}>
      <TextArea
        aria-label={props.ariaLabel}
        value={text}
        onChange={(v) => setText(v)}
        onBlur={() => commit(text)}
        isDisabled={props.disabled}
        rows={12}
        placeholder={props.placeholder}
        className="font-mono text-xs"
      />
      {error && (
        <Text size="xs" className="text-destructive mt-1.5">
          {error}
        </Text>
      )}
    </FieldSection>
  );
}

/**
 * Editor for `metadata.headerExtractors` — reads tokens OUT of a captured
 * session to build request headers. The mirror image of SessionSeedsEditor.
 */
function HeaderExtractorsEditor(props: {
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}) {
  return (
    <JsonSpecEditor
      value={props.value}
      onChange={props.onChange}
      disabled={props.disabled}
      ariaLabel="Header extractors JSON"
      placeholder={EXTRACTOR_PLACEHOLDER}
      title="Auth Handoff (Header Extractors)"
      help="Forward auth headers from the captured browser session — Firebase, Auth0, and other SPA tokens that live in localStorage, sessionStorage, or IndexedDB. Keycloak/oidc-client-ts apps keep oidc.user:<authority>:<client_id> in sessionStorage (jsonPath $.access_token, prefix Bearer, refresh oidc-client-ts). Each entry produces one request header on API-sequence steps using this credential. Leave empty if cookies alone are enough."
      validate={validateExtractorShape}
      emptyLabel="Expected a JSON array of extractor objects."
    />
  );
}

/** Validates each entry against the shared server-side Zod schema. */
function validateExtractorShape(parsed: unknown[]): string | null {
  for (const [i, entry] of parsed.entries()) {
    const result = headerExtractorSchema.safeParse(entry);
    if (!result.success) {
      const issue = result.error.issues[0];
      const path = issue.path.join('.');
      return `Entry ${i}: ${path ? `"${path}" — ` : ''}${issue.message}`;
    }
  }
  return null;
}
